import React, { useState } from "react";
import { Lock } from "lucide-react";

// Sprint 8.A.2 — 4×4 Heatmap: Fokustyp (Zeile) × Konflikt-Thema (Spalte).
// Farb-Intensität via rgba(69, 99, 187, alpha) — Russell-Brand #4563BB.
// Free-User sieht Top-3-Muster regulär; restliche Zellen mit opacity 0.3 + Lock.

// Sprint M5.36 (29.07.2026) — Issue 13: Reihenfolge Katze/Eule/Biene/Hund
// (verbindliche 4er-Matrix, siehe /app/frontend/src/lib/focusRoles.js).
const FOKUSTYPEN = ["Katze", "Eule", "Biene", "Hund"];
const THEMES = ["Führung", "Team", "Familie", "Selbstkritik"];

function getColor(intensity) {
  // Russell-Brand-Primary #4563BB, alpha interpoliert
  return `rgba(69, 99, 187, ${0.15 + intensity * 0.75})`;
}

export default function CockpitHeatmap({ data, isPro, topMuster, onLockedClick }) {
  const [hoverCell, setHoverCell] = useState(null);

  const matrix = data || {};
  const flat = FOKUSTYPEN.flatMap((ft) =>
    THEMES.map((th) => matrix[ft]?.[th] || 0),
  );
  const max = Math.max(...flat, 1);

  // Bestimme Top-3-Zellen (für Free-User: nur diese unverdeckt)
  const ranked = FOKUSTYPEN.flatMap((ft) =>
    THEMES.map((th) => ({ ft, th, count: matrix[ft]?.[th] || 0 })),
  )
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map((c) => `${c.ft}-${c.th}`);
  const visibleSet = new Set(ranked);

  return (
    <div data-testid="cockpit-heatmap" className="cockpit-heatmap">
      {/* Header-Row (Themen) */}
      <div className="grid grid-cols-[80px_repeat(4,1fr)] gap-1 mb-1">
        <div></div>
        {THEMES.map((th) => (
          <div
            key={th}
            className="text-[10px] font-semibold text-slate-500 text-center uppercase tracking-wider"
            data-testid={`heatmap-col-${th}`}
          >
            {th}
          </div>
        ))}
      </div>

      {/* Body */}
      {FOKUSTYPEN.map((ft) => (
        <div
          key={ft}
          className="grid grid-cols-[80px_repeat(4,1fr)] gap-1 mb-1"
        >
          <div
            className="text-xs font-bold text-slate-700 self-center pr-2 text-right"
            data-testid={`heatmap-row-${ft}`}
          >
            {ft}
          </div>
          {THEMES.map((th) => {
            const value = matrix[ft]?.[th] || 0;
            const intensity = max > 0 ? value / max : 0;
            const cellKey = `${ft}-${th}`;
            const isVisible = isPro || visibleSet.has(cellKey);
            const isTop = topMuster && topMuster.fokustyp === ft && topMuster.theme === th;

            return (
              <button
                key={th}
                type="button"
                data-testid={`heatmap-cell-${ft}-${th}`}
                onMouseEnter={() => setHoverCell(cellKey)}
                onMouseLeave={() => setHoverCell(null)}
                onClick={() => {
                  if (!isVisible && onLockedClick) onLockedClick();
                }}
                title={
                  isVisible
                    ? `${ft} ↔ ${th}: ${value} Flexpfad${value === 1 ? "" : "e"}`
                    : "Mit Pro Solo alle Werkzeuge im Detail"
                }
                className={`relative aspect-square rounded-md transition-all duration-200 hover:ring-2 hover:ring-[#4563BB] hover:scale-105 ${
                  isVisible ? "" : "opacity-30 cursor-pointer"
                } ${isTop ? "ring-2 ring-[#4563BB] ring-offset-1" : ""}`}
                style={{ backgroundColor: getColor(intensity) }}
              >
                {isVisible ? (
                  value > 0 ? (
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
                      {value}
                    </span>
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400">
                      ·
                    </span>
                  )
                ) : (
                  <Lock className="absolute inset-0 m-auto w-3 h-3 text-slate-500" />
                )}
              </button>
            );
          })}
        </div>
      ))}

      {/* Hover-Tooltip */}
      {hoverCell && (
        <p
          data-testid="heatmap-tooltip"
          className="mt-2 text-xs text-slate-500"
        >
          {(() => {
            const [ft, th] = hoverCell.split("-");
            const v = matrix[ft]?.[th] || 0;
            const visible = isPro || visibleSet.has(hoverCell);
            if (!visible) return "🔒 Mit Pro Solo alle Werkzeuge im Detail.";
            return `${ft} ↔ ${th}: ${v} Flexpfad${v === 1 ? "" : "e"}`;
          })()}
        </p>
      )}

      {/* Legend */}
      <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-400">
        <span>selten</span>
        <div className="flex gap-0.5">
          {[0.1, 0.3, 0.5, 0.7, 0.9].map((i) => (
            <div
              key={i}
              className="w-4 h-2 rounded-sm"
              style={{ backgroundColor: getColor(i) }}
            />
          ))}
        </div>
        <span>häufig</span>
      </div>
    </div>
  );
}
